import React, { useState, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { 
  QuestionCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Button,
  Modal,
  Input,
  Form,
  Row,
  Col,
  Select,
  TimePicker,
  Avatar,
  Checkbox,
  Tooltip,
  AutoComplete,
} from "antd";
import moment from "moment-timezone";
import Notification from "../Notification/Notification";
import showConfirmModal from "../ModalConfirm/ModalConfirm";
import { useAppointmentSearchServices, useSearchPatient } from "../../API/apiUtils";
import FormatDate from "../FormatDate/FormatDate";
import AppointmentDatePicker from "../BaseDatePicker/AppointmentDatePicker";
import { phoneValidator } from "../../utils/validator";
import BaseDatePicker from "../BaseDatePicker/BaseDatePicker";
import { doPost, doPatch, doDelete } from "../../API/apis";

const { Option } = Select;
const TIME_FORMAT = "h:mm a";

const SUCCESS_MESSAGES = {
  update: "Appointment Updated Successfully",
  add: "Appointment Created Successfully",
};

const DURATION_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
];

// Helper function to create a correct time object from a date
const createTimeObject = (date, timezone) => {
  let timeObj;
  
  if (date instanceof Date) {
    const dateTime = DateTime.fromJSDate(date, { zone: timezone });
    timeObj = {
      hour: dateTime.hour,
      minute: dateTime.minute,
      displayTime: dateTime.toFormat("hh:mm a"),
      dateTime: dateTime
    };
  } else if (moment.isMoment(date)) {
    const momentDate = date.clone().tz(timezone);
    timeObj = {
      hour: momentDate.hours(),
      minute: momentDate.minutes(),
      displayTime: momentDate.format("h:mm a"),
      dateTime: momentDate
    };
  } else if (date instanceof DateTime) {
    timeObj = {
      hour: date.hour,
      minute: date.minute,
      displayTime: date.toFormat("hh:mm a"),
      dateTime: date
    };
  } else {
    console.warn("Unknown date type provided to createTimeObject");
    const now = DateTime.now().setZone(timezone);
    timeObj = {
      hour: now.hour,
      minute: now.minute,
      displayTime: now.toFormat("hh:mm a"),
      dateTime: now
    };
  }
  
  const dayjsTime = dayjs()
    .hour(timeObj.hour)
    .minute(timeObj.minute)
    .second(0);
    
  return {
    ...timeObj,
    dayjsTime
  };
};

const AppointmentModal = ({
  isVisible,
  onClose,
  edit,
  selectedAppointment,
  appointmentDate,
  appointmentTime,
  appointmentWith,
  providers,
  officeTimezone,
  onAppointmentSuccess,
  onDelete,
  initialFormData = {}
}) => {
  const [form] = Form.useForm();
  const [submit, setSubmit] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [patientName, setPatientName] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [isNotRegistered, setIsNotRegistered] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const { options, handleSearchServices } = useAppointmentSearchServices();
  const { options: nameOptions, handleSearchPatient } = useSearchPatient(({ fullName }) => fullName);

  // Memoize patient name to avoid recalculation
  const patient_name = useMemo(() => {
    if (clientDetails) {
      return `${clientDetails.first_name || ''} ${clientDetails.middle_name || ''} ${clientDetails.last_name || ''}`.trim();
    }
    return '';
  }, [clientDetails]);

  const disabledDate = useCallback((current) => current && current > new Date(), []);

  const handleProviderSearch = useCallback((value) => {
    if (!providers) return;
    
    const filteredSuggestions = providers.filter((suggestion) =>
      suggestion.name.toLowerCase().includes(value.toLowerCase())
    );
    setSuggestions(filteredSuggestions);
  }, [providers]);

  const handleSelect = useCallback((value, option) => {
    setSelectedProvider({
      id: option.key,
      name: option.children, 
    });
  }, []);

  const notRegistered = useCallback((e) => {
    setIsNotRegistered(e.target.checked);
    
    if (e.target.checked) {
      form.setFields([
        {
          name: 'patient_name',
          value: undefined
        }
      ]);
      setClientDetails(null);
      setPatientName(null);
    }
  }, [form]);

  const handleFormValuesChange = useCallback((changedValues, allValues) => {
    const relevantFields = ['appointment_date', 'start_time', 'duration', 'appointment_with'];
    const hasRelevantChanges = relevantFields.some(field => field in changedValues);
    
    if (!hasRelevantChanges) return;
    
    const selectedDate = allValues.appointment_date;
    const provider = allValues.appointment_with;
    const selectedStartTime = allValues.start_time || dayjs().startOf("day");
    const selectedDuration = allValues.duration || 15;

    if (selectedDate) {
      const formattedDate = dayjs(selectedDate).format("MM-DD-YYYY");
      const hour24 = selectedStartTime.hour();
      const minute = selectedStartTime.minute();
      
      const formattedStartTime = selectedStartTime.format("h:mm a");
      const formattedEndTime = selectedStartTime
        .add(selectedDuration, "minutes")
        .format("h:mm a");

      setStartTime(formattedStartTime);
      
    }
  }, []);

  const handleCancel = useCallback(() => {
    setClientDetails(null);
    setIsNotRegistered(false);
    setPatientName(null);
    setSelectedProvider(null);
    
    setTimeout(() => {
      form.resetFields();
    }, 200);
    
    onClose();
  }, [form, onClose]);

  const deleteAppointment = useCallback(async (record) => {
    try {
      const response = await doDelete(`/appointments/delete/${record}/`);
      if (response.status === 200) {
        Notification.success("Appointment deleted successfully");
        onAppointmentSuccess();
        handleCancel();
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
    }
  }, [onAppointmentSuccess, handleCancel]);

  const handleDelete = useCallback((selectedAppointment) => {
    showConfirmModal({
      title: "Confirm Delete?",
      icon: null,
      content: "Are you sure you want to delete this?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk() {
        deleteAppointment(selectedAppointment);
      },
    });
  }, [deleteAppointment]);

  const onSelect = useCallback((value, option) => {
    // Extract patient ID from the value (format: "id|name")
    const patientId = value.split('-')[0];
    
    // Find the selected patient from nameOptions using the ID
    const selectedClient = nameOptions.find((item) => item.id === parseInt(patientId));
    if (!selectedClient) return;

    setIsNotRegistered(false);
    
    form.setFieldsValue({
      unregistered_patient: false
    });

    setClientDetails({
      patient_id: selectedClient.id,
      first_name: selectedClient.first_name,
      middle_name: selectedClient.middle_name,
      last_name: selectedClient.last_name,
      patient_MRN: selectedClient.mrn_number,
      dob: selectedClient.date_of_birth,
      contact: selectedClient.patient_phone,
      gender: selectedClient.gender,
      profile_picture: selectedClient.profile_picture,
    });
  }, [nameOptions, form]);

  const handleServiceSelect = (value) => {
    const selectedOption = options.find((option) => option.value === value);
    if (selectedOption) {
      form.setFieldsValue({service: selectedOption?.service_name})
    }
  };

  const handleAppointmentSchedule = useCallback(async (values) => {
    setSubmit(true);
    
    try {
      const {
        first_name,
        middle_name,
        last_name,
        phone,
        start_time,
        duration,
        service,
        reason_for_visit,
        dob,
      } = values;
      
      let appointment_dateTime;
      
      if (edit) {
        // For updates, check if date/time fields have actually changed
        const originalDate = initialFormData?.appointment_date;
        const originalStartTime = initialFormData?.start_time;
        

        
        // If date and time haven't changed, preserve the original appointment_date
        if (originalDate && originalStartTime && 
            values.appointment_date?.isSame(originalDate, 'day') && 
            values.start_time?.isSame(originalStartTime, 'minute')) {
          console.log('Date and time unchanged - preserving original appointment date');
          // Use the original appointment date from the event data
          // This prevents timezone conversion issues when only updating other fields
          if (initialFormData.original_appointment_date) {
            // Use the original appointment date from the event (already in UTC)
            appointment_dateTime = initialFormData.original_appointment_date;
            console.log('Using original appointment date:', appointment_dateTime);
          } else {
            console.log('No original appointment date available, using fallback');
            // Fallback: create appointment date from current form values without timezone conversion
            const apt_date = values?.appointment_date?.format("YYYY-MM-DD");
            const hour24 = start_time.hour();
            const minute = start_time.minute();
            const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            
            // Create the date in the office timezone first, then convert to UTC
            appointment_dateTime = moment
              .tz(`${apt_date} ${timeString}`, officeTimezone)
              .utc()
              .format();
            console.log('Created fallback appointment date:', appointment_dateTime);
          }
        } else {
          console.log('Date or time changed - creating new appointment date');
          // Date or time has changed, create new appointment_dateTime
          const apt_date = values?.appointment_date?.format("YYYY-MM-DD");
          const hour24 = start_time.hour();
          const minute = start_time.minute();
          const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          
          appointment_dateTime = moment
            .tz(`${apt_date} ${timeString}`, officeTimezone)
            .utc()
            .format();
        }
      } else {
        // For new appointments, create new appointment_dateTime
        const apt_date = values?.appointment_date?.format("YYYY-MM-DD");
        const hour24 = start_time.hour();
        const minute = start_time.minute();
        const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        appointment_dateTime = moment
          .tz(`${apt_date} ${timeString}`, officeTimezone)
          .utc()
          .format();
      }
      
      const hour24 = start_time.hour();
      const minute = start_time.minute();
      const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      // Preserve original status in edit mode, use default for new appointments
      let current_status;
      if (edit) {
        const originalStatus = initialFormData?.current_status;
        // Always preserve the original status when editing
        current_status = originalStatus || "Scheduled";
      } else {
        current_status = "Scheduled";
      }
      
      const data = {
        patient_id: clientDetails?.patient_id,
        first_name: clientDetails?.first_name || first_name,
        middle_name: clientDetails?.middle_name || middle_name,
        last_name: clientDetails?.last_name || last_name,
        patient_MRN: clientDetails?.patient_MRN,
        gender: clientDetails?.gender,
        dob: clientDetails?.dob || dob,
        contact: clientDetails?.contact || phone,
        appointment_date: appointment_dateTime,
        time_zone_value: officeTimezone,
        time: `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`,
        duration: duration || 30,
        service: service,
        appointment_with: selectedProvider?.name,
        provider_id: selectedProvider?.id,
        visit_reason: reason_for_visit,
        current_status: current_status,
      };
      
      const response = edit
        ? await doPatch(`/appointments/update/${selectedAppointment}/`, data)
        : await doPost("/appointments/make/", data);
      
      if (response.status === 201 || response.status === 200) {
        Notification.success(edit ? SUCCESS_MESSAGES.update : SUCCESS_MESSAGES.add);
        onAppointmentSuccess();
        handleCancel();
      } else if (response.status === 406) {
        Notification.warning("Provider not availble in this duration");
      } else if (response.status === 400) {
        Notification.error("Appointment Date can not be in the past");
      }
    } catch (error) {
      console.error("Error scheduling appointment:", error);
      Notification.error("Something Went Wrong");
    } finally {
      setSubmit(false);
    }
  }, [clientDetails, edit, onAppointmentSuccess, handleCancel, officeTimezone, selectedAppointment, selectedProvider, initialFormData]);

  // Render avatar with fallback
  const renderAvatar = () => {
    const hasProfilePicture = clientDetails?.profile_picture;
    const firstInitial = clientDetails?.first_name?.[0] || 'A';
    
    return (
      <Avatar
        size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}
        src={hasProfilePicture ? clientDetails.profile_picture : null}
      >
        {!hasProfilePicture && firstInitial.toUpperCase()}
      </Avatar>
    );
  };

  // Render patient info section
  const renderPatientInfo = () => {
    const displayName = clientDetails?.first_name
      ? patient_name
      : edit
      ? patientName
      : "Search Patient";
      
    return (
      <div className="left">
        {renderAvatar()}
        <p style={{ fontSize:'16px' }} className="text-white font-semibold">
          {displayName}
        </p>
        {clientDetails?.dob && (
          <p style={{ fontSize:'14px' }} className="text-white">
            DOB: <FormatDate date={clientDetails.dob} />
          </p>
        )}
        <hr width="80%" />
        <p className="text-white" style={{fontSize:'16px'}}>Selected Date and Time</p>
        <p className="font-semibold" style={{fontSize:'16px'}}>
          <FormatDate date={appointmentDate} />
        </p>
        <p className="font-semibold" style={{fontSize:'16px'}}>{appointmentTime || "Appointment Time"}</p>
        <hr width="80%" />
        <p className="text-white" style={{fontSize:'16px'}}>Appointment With</p>
        <p className="text-white" style={{fontSize:'16px'}}> {appointmentWith || "Provider Name"}</p>
      </div>
    );
  };

  // Render unregistered patient fields
  const renderUnregisteredFields = () => {
    if (!isNotRegistered) return null;
    
    return (
      <>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="first_name" label="First Name">
              <Input placeholder="First Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="last_name" label="Last Name">
              <Input placeholder="Last Name" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item name="dob" label="DOB">
              <BaseDatePicker disabledDate={disabledDate} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="phone" 
              label="Phone"
              rules={[{ validator: phoneValidator }]}
            >
              <Input placeholder="Phone" maxLength={20} />
            </Form.Item>
          </Col>
        </Row>
      </>
    );
  };

  // Initialize form data when modal opens or edit data changes
  React.useEffect(() => {
    if (isVisible && initialFormData && Object.keys(initialFormData).length > 0) {
      // Set form values
      form.setFieldsValue(initialFormData);
      
      // Set component state based on initial data
      if (initialFormData.unregistered_patient) {
        setIsNotRegistered(true);
        setPatientName(initialFormData.patient_name);
      } else {
        setIsNotRegistered(false);
        if (initialFormData.patient_name) {
          setPatientName(initialFormData.patient_name);
        }
      }
    }
  }, [isVisible, initialFormData, form]);

  return (
    <Modal
      open={isVisible}
      closeIcon={
        <Button size="small" className="app-close-icon" shape="round">
          Close
        </Button>
      }
      onCancel={handleCancel}
      footer={null}
      width={680}
      maskClosable={false}
      className="appointment_modal"
    >
      <div className="appointment_box">
        {renderPatientInfo()}
        
        <Form
          layout="vertical"
          form={form}
          onValuesChange={handleFormValuesChange}
          onFinish={handleAppointmentSchedule}
          className="appointment-form"
        >
          <div className="right-header">
            <p className="mb-4">
              {edit ? "Update Appointment" : "Book a New Appointment"}
            </p>
          </div>
          
          <Form.Item
            name="patient_name"
            label="Search Patient"
            tooltip="If No Patient Found Please Select New Unregistered Patient"
          >
            <AutoComplete
              showSearch
              options={nameOptions}
              allowClear
              onSelect={onSelect}
              onSearch={handleSearchPatient}
              placeholder="Search for existing Patient"
              filterOption={false}
              notFoundContent={!nameOptions.length ? "No matches found" : null}
            />
          </Form.Item>
          
          <Form.Item name="unregistered_patient" valuePropName="checked">
            <Checkbox onChange={notRegistered}>
              New Unregistered Patient or Client
              <Tooltip title="Check the box if the Patient or Client is not registered">
                <QuestionCircleOutlined className="tooltip__icon" />
              </Tooltip>
            </Checkbox>
          </Form.Item>
          
          {renderUnregisteredFields()}
          
          <div className="right-header">
            <p className="mb-4">Appointment Details</p>
          </div>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="appointment_date"
                label="Date"
                rules={[{ required: true, message: "Please Select Appointment Date" }]}
              >
                <AppointmentDatePicker value={appointmentDate} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="start_time"
                label="Start Time"
                rules={[{ required: true, message: "Start Time is Required" }]}
              >
                <TimePicker use12Hours format={TIME_FORMAT} minuteStep={5} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration" label="Duration" initialValue="30">
                <Select placeholder="Duration">
                  {DURATION_OPTIONS.map(option => (
                    <Option key={option.value} value={option.value}>{option.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="service"
            label="Service Name"
            rules={[{ required: true, message: "Service is Required" }]}
          >
            <AutoComplete
              options={options}
              allowClear
              onSelect={handleServiceSelect}
              width={"100%"}
              onSearch={handleSearchServices}
              placeholder="Search for service name"
            />
          </Form.Item>
          
          <Row>
            <Col span={12}>
              <Form.Item
                name="appointment_with"
                label="Provider Name"
                rules={[{ required: true, message: "Provider is Required" }]}
              >
                <Select
                  showSearch
                  placeholder="Search for providers"
                  style={{width:'394px'}}
                  onSearch={handleProviderSearch}
                  onSelect={handleSelect}
                  optionFilterProp="children"
                  filterOption={false}
                >
                  {suggestions?.map((doctor) => (
                    <Option key={doctor.id} value={doctor.name}>
                      {doctor.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="reason_for_visit"
            label="Reason"
            rules={[{ required: true, message: "Reason For Visit is Required" }]}
          >
            <Input.TextArea
              placeholder="Reason for visit"
              maxLength={300}
              showCount
            />
          </Form.Item>
          
          <div className="flex justify-between mt-[1.4rem]">
            <Button onClick={handleCancel}>Cancel</Button>

            <div>
              {edit && (
                <Button
                  className="danger__button mr-2"
                  onClick={() => handleDelete(selectedAppointment)}
                >
                  Delete
                </Button>
              )}
              <Button type="primary" htmlType="submit" loading={submit}>
                {edit ? "Update" : "Book Appointment"}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AppointmentModal; 